import { useCallback, useEffect, useState } from "react";
import { FiArrowDownLeft, FiArrowUpRight, FiClock, FiLoader, FiX } from "react-icons/fi";
import { getAdminUser } from "../api/admin";

/**
 * One user's money, end to end: what they earned, what is still waiting out its hold,
 * what they withdrew and what they bought.
 *
 * The question this answers is the one an admin has in front of them — is this person
 * owed anything, what earned it, and has it been paid — without opening a database.
 *
 * @see mintly-back/src/controllers/adminUsersController.getUser
 */

const money = (n, currency = "GEL") =>
  `${currency === "GEL" ? "₾" : `${currency} `}${Number(n || 0).toFixed(2)}`;

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

/** Payout states, in the words someone reading this would use. */
const PAYOUT_LABEL = {
  requested: "requested",
  INITIAL: "sent to bank",
  PENDING: "sent to bank",
  SUCCESS: "paid",
  REJECTED: "rejected",
  FAILED: "failed",
  DELETED: "cancelled",
  INITIAL_INSUFFICIENT_BALANCE: "not enough float",
};

const AdminUserDetail = ({ userId, isDark, onClose, setToast }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ledger");

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const panel = "bg-bg-raised border-separator-default";
  const rowBorder = "border-separator-default";

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await getAdminUser(userId);
      setData(res.data);
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not load this user");
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [userId, setToast, onClose]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const user = data?.user;
  const tabs = [
    ["ledger", `Money in and out${data ? ` (${data.ledger.length})` : ""}`],
    ["payouts", `Withdrawals${data ? ` (${data.payouts.length})` : ""}`],
    ["purchases", `Bought${data ? ` (${data.purchases.length})` : ""}`],
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="User detail"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className={`my-8 w-full max-w-4xl overflow-hidden rounded-2xl border shadow-2xl ${panel}`}>
        <div className={`flex items-start justify-between gap-4 border-b px-6 py-4 ${rowBorder}`}>
          <div className="min-w-0">
            <h3 className={`text-lg font-bold ${baseText}`}>
              {user?.username || "User"}
              {user?.blocked && (
                <span className="ml-2 rounded-lg bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-400">
                  blocked
                </span>
              )}
            </h3>
            <p className={`text-xs ${mutedText}`}>
              {user?.email}
              {user ? ` · joined ${when(user.createdAt).split(",")[0]}` : ""}
              {user ? ` · ${user.trustLevelKey} ${user.trustLevelLabel}` : ""}
              {user ? ` · ${user.holdDays === 0 ? "no hold" : `${user.holdDays}-day hold`}` : ""}
            </p>
            {user?.mintSpaces?.length > 0 && (
              <p className={`mt-1 text-[11px] ${mutedText}`}>
                Sells through: {user.mintSpaces.map((s) => s.name).join(", ")}
              </p>
            )}
            {(user?.invitedBy || user?.invitedCount > 0) && (
              <p className={`mt-1 text-[11px] ${mutedText}`}>
                {user.invitedBy ? `Invited by ${user.invitedBy.username}` : ""}
                {user.invitedBy && user.invitedCount > 0 ? " · " : ""}
                {user.invitedCount > 0
                  ? `Invited ${user.invitedCount} ${user.invitedCount === 1 ? "person" : "people"}`
                  : ""}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-lg p-2 hover:bg-fill-quaternary`}
            aria-label="Close"
          >
            <FiX className={`h-5 w-5 ${mutedText}`} />
          </button>
        </div>

        {loading || !data ? (
          <div className={`flex items-center justify-center gap-2 py-16 ${mutedText}`}>
            <FiLoader className="h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid gap-3 px-6 py-5 sm:grid-cols-4">
              {[
                ["Available", money(data.balance.available)],
                ["On hold", money(data.balance.onHold)],
                ["Credited (shown below)", money(data.balance.earnedInWindow)],
                ["Withdrawn", money(data.balance.paidOut)],
              ].map(([label, value]) => (
                <div key={label} className={`rounded-xl border p-4 ${rowBorder} ${isDark ? "bg-white/[0.025]" : "bg-slate-50/70"}`}>
                  <div className={`text-[11px] ${mutedText}`}>{label}</div>
                  <div className={`mt-1 text-xl font-bold tracking-tight ${baseText}`}>{value}</div>
                </div>
              ))}
            </div>

            <div className={`flex gap-2 border-b px-6 ${rowBorder}`}>
              {tabs.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-semibold transition ${
                    tab === id ? "border-mint text-mint" : `border-transparent ${mutedText}`
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="max-h-[50vh] overflow-y-auto px-6 py-4">
              {tab === "ledger" && (
                <table className="w-full text-sm">
                  <tbody>
                    {data.ledger.map((row) => (
                      <tr key={row.id} className={`border-b ${rowBorder}`}>
                        <td className="py-3 pr-3 w-8">
                          {row.direction === "out" ? (
                            <FiArrowUpRight className="h-4 w-4 text-rose-400" />
                          ) : (
                            <FiArrowDownLeft className="h-4 w-4 text-mint" />
                          )}
                        </td>
                        <td className={`py-3 pr-4 ${baseText}`}>
                          <div className="font-semibold">{row.label}</div>
                          {row.detail && (
                            <div className={`text-[11px] ${mutedText}`}>{row.detail}</div>
                          )}
                        </td>
                        <td className={`py-3 pr-4 whitespace-nowrap text-[11px] ${mutedText}`}>
                          {when(row.date)}
                        </td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          {row.held ? (
                            <span className={`inline-flex items-center gap-1 text-[11px] ${mutedText}`}>
                              <FiClock className="h-3 w-3" /> on hold
                            </span>
                          ) : row.releasedAt ? (
                            <span className={`text-[11px] ${mutedText}`}>
                              released {when(row.releasedAt).split(",")[0]}
                            </span>
                          ) : null}
                        </td>
                        <td
                          className={`py-3 text-right font-semibold whitespace-nowrap ${
                            row.direction === "out" ? "text-rose-400" : baseText
                          }`}
                        >
                          {row.direction === "out" ? "−" : "+"}
                          {money(Math.abs(row.amount))}
                        </td>
                      </tr>
                    ))}
                    {data.ledger.length === 0 && (
                      <tr>
                        <td colSpan={5} className={`py-10 text-center ${mutedText}`}>
                          No money has moved on this account.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {tab === "payouts" && (
                <table className="w-full text-sm">
                  <tbody>
                    {data.payouts.map((p) => (
                      <tr key={p.id} className={`border-b ${rowBorder}`}>
                        <td className={`py-3 pr-4 ${baseText}`}>
                          <div className="font-semibold">{money(p.amount, p.currency)}</div>
                          <div className={`text-[11px] ${mutedText}`}>
                            {p.beneficiary} {p.iban}
                          </div>
                        </td>
                        <td className={`py-3 pr-4 text-[11px] ${mutedText}`}>
                          <div>requested {when(p.date)}</div>
                          {p.settledAt && <div>settled {when(p.settledAt)}</div>}
                        </td>
                        <td className="py-3 text-right">
                          <span
                            className={`rounded-lg px-2 py-1 text-[11px] font-semibold ${
                              p.status === "SUCCESS"
                                ? "bg-mint/15 text-mint"
                                : ["REJECTED", "FAILED", "INITIAL_INSUFFICIENT_BALANCE"].includes(p.status)
                                  ? "bg-rose-500/15 text-rose-400"
                                  : "bg-fill-tertiary text-label-secondary"
                            }`}
                          >
                            {PAYOUT_LABEL[p.status] || p.status}
                          </span>
                          {p.failReason && (
                            <div className="mt-1 text-[11px] text-rose-400">{p.failReason}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {data.payouts.length === 0 && (
                      <tr>
                        <td colSpan={3} className={`py-10 text-center ${mutedText}`}>
                          They have never withdrawn.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {tab === "purchases" && (
                <table className="w-full text-sm">
                  <tbody>
                    {data.purchases.map((p) => (
                      <tr key={p.id} className={`border-b ${rowBorder}`}>
                        <td className={`py-3 pr-4 ${baseText}`}>
                          <div className="font-semibold">{p.product}</div>
                          <div className={`text-[11px] ${mutedText}`}>{p.mintSpace}</div>
                        </td>
                        <td className={`py-3 pr-4 text-[11px] ${mutedText}`}>{when(p.date)}</td>
                        <td className={`py-3 pr-4 text-[11px] ${mutedText}`}>
                          {p.status}
                          {p.trialEndsAt ? ` · until ${when(p.trialEndsAt).split(",")[0]}` : ""}
                        </td>
                        <td className={`py-3 text-right font-semibold ${baseText}`}>
                          {money(p.amount, p.currency)}
                          {p.refunded > 0 && (
                            <div className="text-[11px] text-rose-400">
                              {money(p.refunded)} refunded
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {data.purchases.length === 0 && (
                      <tr>
                        <td colSpan={4} className={`py-10 text-center ${mutedText}`}>
                          They have never bought anything.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUserDetail;
